import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { ShopHeroReveal } from "./ShopHeroReveal";
import "./shopHero.css";

export interface ShopHeroScrollCueProps {
  /** `id` de la sección a la que baja. La pone `ShopPage`. */
  targetId: string;
  animate: boolean;
  /** Retardo de su aparición, para que llegue el último. */
  delay?: number;
}

/**
 * La flecha de bajar. Es un control, no un adorno.
 *
 * ── Por qué es un enlace y no un `<div>` con `onClick` ───────────────
 * Una flecha decorativa que no hace nada es peor que ninguna: promete una
 * acción, la persona la pulsa y no pasa nada. Esto es un `<a href="#…">`
 * de verdad —vía el `as` polimórfico de `Button`—, así que se tabula,
 * se activa con Intro, se abre con la rueda del ratón, sale en el menú
 * contextual y funciona aunque el JavaScript se caiga.
 *
 * ── Tiene texto visible, no solo `aria-label` ────────────────────────
 * «Ver los productos» se lee. Un botón redondo con una flecha y un
 * `aria-label` escondido funciona para el lector de pantalla y falla para
 * quien simplemente no sabe qué va a pasar si lo pulsa. El nombre
 * accesible y el nombre visible son el mismo, que además es lo que exige
 * el criterio 2.5.3 de la WCAG.
 *
 * ── El clic se intercepta por una razón concreta ─────────────────────
 * `globals.css` pone `scroll-behavior: smooth` en el `html` para todo el
 * sitio, y un ancla nativa lo obedecería SIEMPRE — también con
 * `prefers-reduced-motion` puesto, que es justo el desplazamiento largo y
 * animado que provoca mareo. Aquí se decide en JavaScript: suave si hay
 * movimiento, instantáneo si no.
 *
 * El `preventDefault` va DESPUÉS de comprobar que el destino existe. Si
 * algún día la rejilla cambia de `id`, el ancla nativa sigue haciendo su
 * trabajo en lugar de convertirse en un botón muerto.
 *
 * ── Y mueve el foco ──────────────────────────────────────────────────
 * Desplazar la ventana no mueve el foco del teclado: sin `focus()`, quien
 * pulsa la flecha con Intro se encuentra mirando los productos mientras
 * el siguiente tabulador vuelve a la portada. `preventScroll` evita que el
 * navegador dé un segundo salto encima del que acabamos de pedir.
 */
export function ShopHeroScrollCue({
  targetId,
  animate,
  delay = 900,
}: ShopHeroScrollCueProps) {
  const { t } = useTranslation();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();
    /*
     * `"instant"`, no `"auto"`. Es una trampa de la especificación:
     * `behavior: "auto"` NO significa "sin animación", significa "haz lo
     * que diga el CSS" — y el CSS de este sitio dice
     * `scroll-behavior: smooth` en el `html`. Con `"auto"` el
     * desplazamiento largo y animado seguía ocurriendo con la preferencia
     * de movimiento reducido puesta, que es exactamente lo que se estaba
     * intentando evitar. Comprobado en Chrome antes de cambiarlo.
     */
    target.scrollIntoView({ behavior: animate ? "smooth" : "instant", block: "start" });
    target.focus({ preventScroll: true });
  };

  return (
    <ShopHeroReveal
      animate={animate}
      delay={delay}
      className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center sm:bottom-8"
    >
      <Button
        as="a"
        href={`#${targetId}`}
        onClick={handleClick}
        variant="inverse"
        size="sm"
        // El anillo de foco del botón se dibuja sobre crema por defecto;
        // aquí el fondo es tinta, y sin este cambio el hueco entre el
        // anillo y el botón se pierde contra el fondo oscuro.
        className="pointer-events-auto focus-visible:ring-offset-ds-ink"
        rightIcon={
          <ChevronDown aria-hidden="true" className={cn(animate && "shop-hero-nudge")} />
        }
      >
        {t("Ver los productos")}
      </Button>
    </ShopHeroReveal>
  );
}
